// ROI text → structured sections.
// Hybrid strategy: if the area has explicit structured fields (roi_scope, roi_investment, …)
// we use them; otherwise we parse the free-form `enhancements_roi` text looking for
// labels like "Scope:", "Investment:", "ROI:", "Timeline:", "Benefits:" (case-insensitive).
// Anything that doesn't match a label falls into the "overview" bucket.

export const ROI_SECTIONS = [
  { key: 'scope',      label: 'Scope of Work',       icon: 'wrench',    color: '#003DA5' },
  { key: 'investment', label: 'Investment',          icon: 'dollar',    color: '#059669' },
  { key: 'expected_return', label: 'Expected Return', icon: 'trending', color: '#B8860B' },
  { key: 'timeline',   label: 'Timeline',            icon: 'clock',     color: '#7C3AED' },
  { key: 'benefits',   label: 'Key Benefits',        icon: 'sparkles',  color: '#E11D48' },
];

// Aliases for each section (case-insensitive label variants users might type).
const ALIASES = {
  scope:            ['scope', 'scope of work', 'what', 'work', 'description'],
  investment:       ['investment', 'capex', 'cost', 'budget', 'cost estimate', 'cost to build'],
  expected_return:  ['expected return', 'return', 'roi', 'payback', 'yield', 'revenue', 'revenue impact'],
  timeline:         ['timeline', 'duration', 'completion', 'schedule', 'time'],
  benefits:         ['benefits', 'impact', 'upside', 'value', 'value add', 'outcome', 'key benefits'],
};

// Build one regex that matches any known label at the start of a line (or after a newline),
// followed by ":" or "—" or "–". Case-insensitive.
const ALL_LABELS = Object.entries(ALIASES).flatMap(([sec, names]) => names.map((n) => ({ sec, name: n })));
ALL_LABELS.sort((a, b) => b.name.length - a.name.length); // longest first so "key benefits" wins over "benefits"

const LABEL_PATTERN = new RegExp(
  '(^|\\n)\\s*(' + ALL_LABELS.map((l) => l.name.replace(/[-/\\^$*+?.()|[\\]{}]/g, '\\$&')).join('|') + ')\\s*[:\\-–—]\\s*',
  'gi'
);

function matchLabelToKey(labelText) {
  const lower = labelText.trim().toLowerCase();
  for (const { sec, name } of ALL_LABELS) {
    if (name === lower) return sec;
  }
  return null;
}

// Parse free-form text into section buckets. Returns { scope, investment, expected_return, timeline, benefits, overview }.
export function parseRoiText(text) {
  const out = { scope: '', investment: '', expected_return: '', timeline: '', benefits: '', overview: '' };
  if (!text || typeof text !== 'string') return out;

  // Find all label matches with their positions.
  const matches = [];
  let m;
  LABEL_PATTERN.lastIndex = 0;
  while ((m = LABEL_PATTERN.exec(text)) !== null) {
    matches.push({ index: m.index + m[1].length, labelEnd: m.index + m[0].length, label: m[2] });
  }

  if (matches.length === 0) {
    out.overview = text.trim();
    return out;
  }

  // Prefix before the first label → overview.
  const firstStart = matches[0].index;
  const prefix = text.slice(0, firstStart).trim();
  if (prefix) out.overview = prefix;

  // For each match, slice from labelEnd up to the next match's index.
  for (let i = 0; i < matches.length; i++) {
    const cur = matches[i];
    const next = matches[i + 1];
    const valueEnd = next ? next.index : text.length;
    const value = text.slice(cur.labelEnd, valueEnd).trim();
    const key = matchLabelToKey(cur.label);
    if (key && value) {
      // If a section appears twice, append with newline separator.
      out[key] = out[key] ? out[key] + '\n' + value : value;
    }
  }

  return out;
}

// Merge structured fields from the area with parsed free-text fallback.
// Structured fields take precedence when non-empty.
export function buildRoiSections(area) {
  const parsed = parseRoiText(area?.enhancements_roi || '');
  const pick = (structured, fallback) => {
    const s = (structured || '').trim();
    return s || fallback || '';
  };
  return {
    scope:           pick(area?.roi_scope,      parsed.scope),
    investment:      pick(area?.roi_investment, parsed.investment),
    expected_return: pick(area?.roi_return,     parsed.expected_return),
    timeline:        pick(area?.roi_timeline,   parsed.timeline),
    benefits:        pick(area?.roi_benefits,   parsed.benefits),
    overview:        parsed.overview, // only from free-text
  };
}

// Does the area have any ROI content at all (structured OR free text)?
export function hasAnyRoi(area) {
  if (!area) return false;
  if ((area.enhancements_roi || '').trim()) return true;
  return ['roi_scope', 'roi_investment', 'roi_return', 'roi_timeline', 'roi_benefits']
    .some((k) => (area[k] || '').trim());
}
